/**
 * Go Gram Em product form: zip validation + cart clear/add + checkout redirect.
 */
(() => {
  const forms = document.querySelectorAll('[data-gg-gram-form]');
  if (!forms.length) return;

  const normalizeZip = (value) => String(value || '').trim();

  const base64UrlEncode = (text) => {
    const bytes = new TextEncoder().encode(text);
    let binary = '';
    for (const byte of bytes) binary += String.fromCharCode(byte);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  // Cart permalinks are the documented way to prefill checkout[email] and carry line item properties.
  const buildCheckoutPermalink = (variantId, properties) => {
    const url = new URL(`/cart/${variantId}:1`, window.location.origin);
    const filled = Object.fromEntries(Object.entries(properties).filter(([, value]) => value));
    if (Object.keys(filled).length) {
      url.searchParams.set('properties', base64UrlEncode(JSON.stringify(filled)));
    }
    if (filled['Sender Email']) {
      url.searchParams.set('checkout[email]', filled['Sender Email']);
    }
    return url.toString();
  };

  forms.forEach((root) => {
    const form = root.matches('form') ? root : root.querySelector('form');
    if (!form) return;

    const allowed = (() => {
      try {
        return JSON.parse(root.dataset.allowedZips || '[]').map(normalizeZip);
      } catch {
        return [];
      }
    })();
    const inSeason = root.dataset.inSeason === 'true';
    const cartAddUrl = root.dataset.cartAddUrl || '/cart/add.js';
    const cartClearUrl = root.dataset.cartClearUrl || '/cart/clear.js';
    const zipInput = root.querySelector('[data-gg-zip]');
    const zipError = root.querySelector('[data-gg-zip-error]');
    const submitBtn = root.querySelector('[data-gg-submit]');
    const status = root.querySelector('[data-gg-status]');
    const priceInputs = root.querySelectorAll('input[name="id"]');

    const setZipState = (ok, showMessage) => {
      if (!zipInput) return ok;
      zipInput.classList.toggle('is-invalid', !ok && showMessage);
      zipInput.setAttribute('aria-invalid', (!ok && showMessage).toString());
      if (zipError) {
        zipError.hidden = ok || !showMessage;
      }
      return ok;
    };

    const zipOk = (showMessage = false) => {
      if (!zipInput) return true;
      const value = normalizeZip(zipInput.value);
      if (!value) {
        setZipState(false, false);
        return false;
      }
      // No list configured yet — accept any non-empty zip.
      const ok = !allowed.length || allowed.includes(value);
      if (ok || showMessage) setZipState(ok, showMessage);
      return ok;
    };

    const ageInput = root.querySelector('[data-gg-age]');
    const ageError = root.querySelector('[data-gg-age-error]');

    const ageOk = (showMessage = false) => {
      if (!ageInput) return true;
      const value = ageInput.value.trim();
      const age = Number(value);
      const ok = /^\d{1,3}$/.test(value) && age >= 1 && age <= 120;
      if (!ok && !showMessage && value.length > 0) return ok;
      const showError = !ok && showMessage && value.length > 0;
      ageInput.classList.toggle('is-invalid', showError);
      ageInput.setAttribute('aria-invalid', showError.toString());
      if (ageError) ageError.hidden = !showError;
      return ok;
    };

    ageInput?.addEventListener('input', () => {
      const digits = ageInput.value.replace(/\D/g, '').slice(0, 3);
      if (digits !== ageInput.value) ageInput.value = digits;
    });
    ageInput?.addEventListener('blur', () => ageOk(true));

    const selectedVariant = () => root.querySelector('input[name="id"]:checked:not(:disabled)');

    const requiredFilled = () => {
      const required = form.querySelectorAll('[required]:not([type="radio"])');
      const fieldsOk = [...required].every((el) => String(el.value || '').trim().length > 0);
      return fieldsOk && Boolean(selectedVariant());
    };

    const syncSubmit = (showZipMessage = false) => {
      const ageValid = ageOk(showZipMessage);
      const zipValid = zipOk(showZipMessage);
      const ok = inSeason && requiredFilled() && ageValid && zipValid;
      if (submitBtn) submitBtn.disabled = !ok;
      return ok;
    };

    root.addEventListener('input', () => syncSubmit(false));
    root.addEventListener('change', (event) => {
      syncSubmit(false);
      if (event.target === zipInput) zipOk(true);
      if (event.target === ageInput) ageOk(true);
    });
    zipInput?.addEventListener('blur', () => zipOk(true));

    if (!inSeason && submitBtn) {
      submitBtn.disabled = true;
    } else {
      syncSubmit(false);
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!syncSubmit(true)) return;

      const selected = selectedVariant();
      if (!selected) return;

      submitBtn.disabled = true;
      if (status) status.textContent = 'Preparing checkout…';

      const properties = {};
      root.querySelectorAll('[data-gg-property]').forEach((el) => {
        const key = el.getAttribute('data-gg-property');
        if (!key) return;
        properties[key] = String(el.value || '').trim();
      });

      try {
        const cleared = await fetch(cartClearUrl, { method: 'POST', headers: { Accept: 'application/json' } });
        if (!cleared.ok) {
          throw new Error('Could not prepare cart');
        }
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(selected.value),
                quantity: 1,
                properties,
              },
            ],
          }),
        });
        if (!response.ok) {
          let message = '';
          try {
            const data = await response.json();
            message = data.description || data.message || '';
          } catch {}
          throw new Error(message || 'Could not add gram to cart');
        }
        if (status) status.textContent = 'Redirecting to payment…';
        window.location.assign(buildCheckoutPermalink(selected.value, properties));
      } catch (error) {
        console.error(error);
        if (status) status.textContent = error.message && error.message !== 'Failed to fetch'
          ? error.message
          : 'Something went wrong. Please try again.';
        submitBtn.disabled = false;
      }
    });

    // Ensure one available price selected
    if (priceInputs.length && !selectedVariant()) {
      const firstAvailable = [...priceInputs].find((i) => !i.disabled);
      if (firstAvailable) {
        firstAvailable.checked = true;
        syncSubmit(false);
      }
    }
  });
})();
